// repositories/OrderBoxRepository.js
import { useAxiosRepo } from '@pinia-orm/axios';
import { useRepo } from 'pinia-orm';
import { errorFromJsonApi } from './_errorFromJsonApi.js';
import { useAuth } from '@/composables/useAuth.js';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import { jsonApiTransformer as dataFormatter } from '@/httpConfig/transformers/jsonApiTransformer.js';
import i18n from '@/i18n';
import OrderBox from '@/models/commerce/OrderBox.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import {
  cancelOrderBoxItem,
  fetchSupplierSuggestionForItem,
  requestReturnForItem,
  suggestSupplierForItem,
  uncancelOrderBoxItem,
} from '@/services/orderBoxItem.service.js';

/**
 * OrderBox Repository - Production Ready
 *
 * Handles all CRUD operations for OrderBoxes with proper error handling
 * and automatic store synchronization via Pinia ORM Axios.
 */
export default class OrderBoxRepository {
  /**
   * Get standard ORM repository
   */
  static repo() {
    return useRepo(OrderBox);
  }

  /**
   * Get Axios-enabled repository for API calls
   */
  static axiosRepo() {
    return useAxiosRepo(OrderBox);
  }

  // =============================================================================
  // QUERY METHODS (Store-level queries)
  // =============================================================================

  /**
   * Get all boxes with optional status filter
   */
  static all(statusFilter = null) {
    const query = this.repo();
    if (statusFilter && Array.isArray(statusFilter)) {
      return query.where('status', (status) => statusFilter.includes(status)).get();
    }
    return query.get();
  }

  /**
   * Get all active order boxes (Draft, Active, Ready, Packed, Shipped)
   */
  static active() {
    return this.repo()
      .where('status', (status) =>
        [OrderBoxStatus.DRAFT, OrderBoxStatus.ACTIVE, OrderBoxStatus.READY, OrderBoxStatus.PACKED, OrderBoxStatus.SHIPPED].includes(status),
      )
      .get();
  }

  /**
   * Get all closed order boxes (Delivered, Cancelled, Closed)
   */
  static closed() {
    return this.repo()
      .where('status', (status) =>
        [OrderBoxStatus.DELIVERED, OrderBoxStatus.CANCELLED, OrderBoxStatus.CLOSED].includes(status),
      )
      .get();
  }

  /**
   * Find box by ID with items loaded
   */
  static findWithItems(id) {
    return this.repo().with('items').find(id);
  }

  /**
   * Get first active box
   */
  static firstActive() {
    return this.repo()
      .where('status', (status) => ['Draft', 'Active'].includes(status))
      .first();
  }

  // =============================================================================
  // CREATE OPERATIONS
  // =============================================================================

  /**
   * Create new order box
   * API: POST /club-memberships/{membershipId}/order-boxes
   * Expected response: { data: { id, number, status, items: [], ... } }
   */
  static async create(boxData) {

    const jsonData = dataFormatter.serialize(boxData, 'order-boxes');
    try {
      const response = await this.axiosRepo().api().post(
        '/order-boxes',
        {
          data: jsonData.data,
          save: true,
        },
      );

      const createdBox = response.response.data.data;
      return this.repo().with('items').find(createdBox.id);
    } catch (error) {
      console.error('Failed to create box:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorCreating');
    }
  }

  // =============================================================================
  // READ OPERATIONS
  // =============================================================================

  /**
   * Fetch all boxes for current membership
   * API: GET /club-memberships/{membershipId}/order-boxes?include=items
   * Caching: Checks if data exists before fetching
   */
  static async fetchAll(force = false) {
    const { membership } = useAuth();

    // Skip fetch if data exists and not forced
    if (!force && this.repo().all().length > 0) {
      return this.repo().all();
    }

    try {
      await this.axiosRepo().api().get(
        `/club-memberships/${membership.value.id}/order-boxes`,
        {
          params: { include: 'summaries' },
          save: true,
        },
      );

      return this.repo().all();
    } catch (error) {
      console.error('Failed to fetch boxes:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorLoading');
    }
  }

  /**
   * Fetch single box by ID with relationships
   * API: GET /order-boxes/{id}?include=items
   */
  static async fetchOne(id, include = 'summaries,items') {

    try {
      await this.axiosRepo().api().get(`/order-boxes/${id}`, {
        params: { include },
        save: true,
      });

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to fetch box:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorLoading');
    }
  }

  /**
   * Fetch items for a specific post
   * API: GET /posts/{postId}/order-box-items
   */
  static async fetchPostItems(postId) {
    try {
      const response = await useAxiosRepo(OrderBoxItem).api().get(
        `/posts/${postId}/order-box-items`,
        { save: false },
      );

      const items = response.response.data.data;
      useRepo(OrderBoxItem).save(items);

      return useRepo(OrderBoxItem)
        .where('postId', parseInt(postId))
        .with('product')
        .get();
    } catch (error) {
      console.error('Failed to fetch post items:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorLoading');
    }
  }

  /**
     * Fetch items for a specific post
     * API: GET /posts/{postId}/order-box-items
     */
  static async fetchBoxItems(boxId) {
    try {
      const response = await useAxiosRepo(OrderBoxItem).api().get(
        `/order-boxes/${boxId}/items`,
        { save: false },
      );

      const items = response.response.data.data;
      useRepo(OrderBoxItem).save(items);
      return this.repo().with('summaries').with('items').find(boxId);
    } catch (error) {
      console.error('Failed to fetch post items:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorLoading');
    }
  }

  // =============================================================================
  // UPDATE OPERATIONS
  // =============================================================================

  /**
   * Update order box details
   * API: PATCH /order-boxes/{id}
   * Expected request: { status?, notes?, ... }
   * Expected response: { data: { id, status, ... } }
   */
  static async update(id, updates) {
    const jsonData = dataFormatter.serialize(updates, 'order-boxes');
    try {
      await this.axiosRepo().api().patch(`/order-boxes/${id}`, {
        data: jsonData.data,
        save: true,
      });

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to update box:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorUpdating');
    }
  }

  /**
   * Ask to ship (ACTIVE → READY, "ready to pack").
   * API: POST /order-boxes/{id}/request-shipping
   * Body: { meta: { cancelOutOfStock: ["<line id>", …] } } — ship-ready pack S-K.
   *
   * `cancelOutOfStock` is her APPROVAL: the ids of the out-of-stock lines the
   * confirm showed her, which the server cancels as her own act and whose held
   * money returns to her balance. Ids go out as strings; empty means "no
   * approval". The server requires that EVERY line at out-of-stock at that moment
   * is in the list, and refuses (cancelling nothing) when one is not — a line ran
   * out while she looked.
   *
   * ⚠ This used to post `{ save: true }` as the BODY. `save` is a pinia-orm-axios
   * CONFIG option (the third argument, and already true by default), so it was
   * never doing anything there and the server ignored it. The body is `meta` now;
   * `save` sits where it belongs.
   *
   * Success: 200, the carton (READY) — saved into the store by `save: true`.
   * Refusal: 409 with `errors[0].code` one of `ship_request.lines_on_the_way`,
   * `.out_of_stock_not_approved`, `.nothing_arrived`, `.not_open`, and
   * `errors[0].detail` HER sentence in her language, printed verbatim by the
   * caller. `errorFromJsonApi` puts it in `.message`; the translated fallback is
   * for a failure that carries no sentence at all (offline, a 500).
   *
   * @param {string|number} id the carton
   * @param {Array<string|number>} [cancelOutOfStock] the approved line ids
   */
  static async requestShipping(id, cancelOutOfStock = []) {
    const approvedIds = Array.isArray(cancelOutOfStock) ? cancelOutOfStock.map(String) : [];

    try {
      await this.axiosRepo().api().post(
        `/order-boxes/${id}/request-shipping`,
        { meta: { cancelOutOfStock: approvedIds } },
        { save: true },
      );

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to request shipping:', error);
      // The TRANSLATED sentence, never the key: `errorFromJsonApi` hands its
      // fallback back verbatim as `.message`, and a caller that prints `.message`
      // (the toast does) would otherwise show her `box.feedback.errorRequestingShipping`.
      throw errorFromJsonApi(error, i18n.global.t('box.feedback.errorRequestingShipping'));
    }
  }

  /**
   * Take her own carton back out of "waiting to ship" (READY → ACTIVE).
   * API: POST /order-boxes/{id}/reopen
   *
   * Mirrors `requestShipping` above, with one difference that is load-bearing:
   * the reversal drops every line the ship request lifted to PACKAGED back to
   * COLLECTED, so the ITEMS have to be re-read. `.with('items')` after a
   * `save: true` write is what does that here, the same shape `requestShipping`
   * uses.
   *
   * A 409 carries the backend's own Arabic sentence and is meant to be printed
   * verbatim — the carton moved on underneath her. `errorFromJsonApi` already
   * prefers `errors.0.detail` over the fallback key, so the caller gets it.
   */
  static async reopen(id) {
    try {
      await this.axiosRepo().api().post(`/order-boxes/${id}/reopen`, {
        save: true,
      });

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to reopen box:', error);
      // Translated, not the key — same defect as `requestShipping` above: the page
      // toasts `.message`, so a key here reached her as raw text.
      throw errorFromJsonApi(error, i18n.global.t('box.feedback.errorReopening'));
    }
  }

  /**
   * «خلصت طلباتي» — she is done ordering into this carton.
   * API: POST /order-boxes/{id}/done-ordering
   *
   * The carton stays ACTIVE and nothing is cancelled or moved: this stamps
   * `doneOrderingAt`, and the server's own add doors refuse new demand from then
   * on. Idempotent on an already closed carton. 200 returns the carton, which
   * `save: true` writes into the store. 409 `done_ordering.not_open` (the carton
   * is not ACTIVE any more) carries her sentence as `errors[0].detail`.
   */
  static async doneOrdering(id) {
    try {
      await this.axiosRepo().api().post(`/order-boxes/${id}/done-ordering`, {}, { save: true });

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to mark the box done ordering:', error);
      // Translated sentence, never the key — see `requestShipping` above.
      throw errorFromJsonApi(error, i18n.global.t('box.feedback.errorDoneOrdering'));
    }
  }

  /**
   * «افتحي الطلبات تاني» — take the done-ordering stamp back (and with it the
   * team's review and her complaint, which the server clears).
   * API: POST /order-boxes/{id}/resume-ordering
   *
   * Hers any time before READY; a READY carton has its own `reopen` door. Idempotent
   * on an open carton. 409 `resume_ordering.not_open` when the carton is not ACTIVE.
   */
  static async resumeOrdering(id) {
    try {
      await this.axiosRepo().api().post(`/order-boxes/${id}/resume-ordering`, {}, { save: true });

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to resume ordering on the box:', error);
      throw errorFromJsonApi(error, i18n.global.t('box.feedback.errorResumingOrdering'));
    }
  }

  /**
   * «في حاجة غلط؟» — her carton-level complaint on the team's review.
   * API: POST /order-boxes/{id}/review-complaint, body { meta: { text } }
   *
   * 1–500 characters (the server answers 422 outside that). A second complaint
   * REPLACES the first. 409 `review_complaint.no_review` when no review was sent
   * or the carton is no longer ACTIVE — its `detail` is her sentence either way.
   *
   * @param {string|number} id the carton
   * @param {string} text what is wrong
   */
  static async reviewComplaint(id, text) {
    try {
      await this.axiosRepo().api().post(
        `/order-boxes/${id}/review-complaint`,
        { meta: { text: String(text ?? '').trim() } },
        { save: true },
      );

      return this.repo().with('items').find(id);
    } catch (error) {
      console.error('Failed to send the review complaint:', error);
      throw errorFromJsonApi(error, i18n.global.t('box.feedback.errorReviewComplaint'));
    }
  }

  // =============================================================================
  // ITEM OPERATIONS
  // =============================================================================

  /**
   * Add items to order box
   * API: POST /order-boxes/{orderBoxId}/product-items/{productId}
   * Expected request: { data: [{ variantId, qty, notes?, depositAmount?, ... }, ...] }
   * Expected response: { data: [OrderBoxItem, ...] }
   *
   * NOTE: this endpoint accepts qty/notes/reservation fields only — it does NOT
   * accept `status` and silently ignores it. Cancellation is now done via the
   * dedicated POST /order-box-items/{id}/cancel endpoint (see cancelItem below).
   * Do not re-introduce `status` (or the legacy `save` flag) into the items
   * array passed in here.
   */
  static async addItems(orderBoxId, productId, items) {
    try {
      const response = await useAxiosRepo(OrderBoxItem).api().post(
        `/order-boxes/${orderBoxId}/product-items/${productId}`,
        { data: items },
      );

      const savedItems = response.response.data.data;

      useRepo(OrderBoxItem).save(savedItems);

      // Refresh box to get updated totals
      return await this.fetchOne(orderBoxId);
    } catch (error) {
      console.error('Failed to add items:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorUpdating');
    }
  }

  /**
   * Cancel a single item via the dedicated JSON:API action.
   * API: POST /order-box-items/{itemId}/cancel
   *
   * The body is optional; when `reason` is provided it is recorded in the
   * audit log on the backend.
   *
   * Returns the raw deserialized resource (plain object with `status`, `qty`,
   * etc. as own enumerable properties). We deliberately do NOT return the
   * Pinia-ORM `find()` model instance here — pinia-orm model instances are
   * class objects whose field accessors aren't all enumerable own properties,
   * so `{...modelInstance}` may not propagate `status`/`qty` back to a Vue
   * `ref<Array>`. The raw object spreads predictably.
   *
   * Errors propagate with `code` set to either ORDER_BOX_ITEM_PLAN_LOCKED
   * (403) or ORDER_BOX_ITEM_STALE_STATE (422) so UI callers can branch.
   */
  static async cancelItem(orderBoxId, itemId, reason) {
    const updated = await cancelOrderBoxItem(itemId, reason);
    useRepo(OrderBoxItem).save(updated);
    // Fire-and-forget the parent-box refresh — it updates totals/relationships
    // but does NOT need to gate the caller's UI flip. Awaiting it routes
    // pinia-orm reactivity through any subscribers (e.g. an editor sheet's
    // deep watcher on a passed-in items array) BEFORE the caller can merge
    // the new attributes locally, which has previously resulted in the local
    // state being reverted to the pre-cancel snapshot.
    if (orderBoxId) {
      this.fetchOne(orderBoxId).catch((err) => {
        console.warn('cancelItem: parent box refresh failed (non-fatal)', err);
      });
    }
    return updated;
  }

  /**
   * Undo cancellation of an OrderBoxItem.
   * API: POST /order-box-items/{itemId}/uncancel
   *
   * Returns the raw deserialized resource. Same error-code contract as
   * cancelItem.
   */
  static async uncancelItem(orderBoxId, itemId, reason) {
    const updated = await uncancelOrderBoxItem(itemId, reason);
    useRepo(OrderBoxItem).save(updated);
    if (orderBoxId) {
      this.fetchOne(orderBoxId).catch((err) => {
        console.warn('uncancelItem: parent box refresh failed (non-fatal)', err);
      });
    }
    return updated;
  }

  /**
   * Tell the club where to find a line nobody could source.
   * API: POST /order-box-items/{itemId}/suggest-supplier
   *
   * Mirrors `cancelItem` above, including the fire-and-forget parent-box
   * refresh: the returned resource already carries the fresh
   * `supplierSuggestion`, so the caller can flip its UI on that without waiting
   * for a box read that only updates totals and relationships.
   *
   * The ONE difference from cancel is what a failure means. Errors carry `code`
   * ORDER_BOX_ITEM_SUGGEST_FORBIDDEN (403 — the line moved past suggesting, so
   * the door should disappear), ORDER_BOX_ITEM_SUGGEST_ROUND_CLOSED (409 — the
   * plan closed under her) or ORDER_BOX_ITEM_SUGGEST_INVALID (422 — no shop
   * named, an id we do not recognise, or a note over 500). The 422's `detail` is
   * the backend's own sentence and callers surface it the way the cancel path
   * does.
   *
   * @param {string|number|null} orderBoxId  parent box, for the totals refresh
   * @param {string|number} itemId
   * @param {{supplierId?: string|number|null, supplierName?: string|null,
   *   note?: string|null, intent?: string|null, anyPrice?: boolean}} suggestion
   * @returns {Promise<object>} the updated item, flat (deserialized) JSON:API
   */
  static async suggestSupplier(orderBoxId, itemId, suggestion) {
    const updated = await suggestSupplierForItem(itemId, suggestion);
    useRepo(OrderBoxItem).save(updated);
    if (orderBoxId) {
      this.fetchOne(orderBoxId).catch((err) => {
        console.warn('suggestSupplier: parent box refresh failed (non-fatal)', err);
      });
    }
    return updated;
  }

  /**
   * Ask to return a delivered line — «أرجّع الصنف».
   * API: POST /order-box-items/{itemId}/request-return
   *
   * Mirrors `suggestSupplier` above: the response already carries the fresh
   * `supplierReturn` payload, so the caller's UI can flip on it without
   * waiting for the fire-and-forget box refresh (totals/relationships only).
   *
   * Errors carry `code` set to ORDER_BOX_ITEM_RETURN_FORBIDDEN (403 — the
   * door was offered on an ineligible line), ORDER_BOX_ITEM_RETURN_ALREADY_OPEN
   * (409 — a second tap) or ORDER_BOX_ITEM_RETURN_INVALID (422 — a bad body).
   * Every one of the three carries the backend's own Arabic sentence in
   * `.detail`, meant to be rendered verbatim.
   *
   * @param {string|number|null} orderBoxId parent box, for the totals refresh
   * @param {string|number} itemId
   * @param {{reason: string, qty: number, note?: string|null,
   *   photoToken?: string|null}} request
   * @returns {Promise<object>} the updated item, flat (deserialized) JSON:API
   */
  static async requestReturn(orderBoxId, itemId, request) {
    const updated = await requestReturnForItem(itemId, request);
    useRepo(OrderBoxItem).save(updated);
    if (orderBoxId) {
      this.fetchOne(orderBoxId).catch((err) => {
        console.warn('requestReturn: parent box refresh failed (non-fatal)', err);
      });
    }
    return updated;
  }

  /**
   * ⚠ CUT — pending the cleanup pass (oos-hold P3-A, owner answer H-O,
   * 2026-09-15). IT HAS NO CALLERS. `ItemSheets.openDetails()` was the only
   * one, and it is gone: the backend deleted the U-40 sparse-fieldset gate, so
   * an answered tip rides `supplierSuggestion` on every ordinary read of the
   * line (brief `2026-09-15-roster-only-tips-once-per-line.md`, change 5).
   * There is nothing left for this to pull in.
   *
   * Left in place rather than deleted inline, per this repo's no-inline-deletes
   * rule.
   *
   * Pull in the club's ANSWER to her tip on one line, and merge it in place.
   *
   * The answer travels only on a request that names the attribute in a sparse
   * fieldset — see `fetchSupplierSuggestionForItem` for the whole reason — so
   * without this call her line keeps showing an open receipt and never the
   * decline reason (gap U-40).
   *
   * ── THE MERGE, AND THE HOOK IT DELIBERATELY DOES NOT TRIP ───────────────────
   * This saves `{ id, supplierSuggestion }` and NOTHING else. `OrderBoxItem`'s
   * `updating` hook nulls every omitted `OMITTED_WHEN_ABSENT` key on any record
   * that mentions `status` (defect U-30) — so a record without `status` is
   * exactly the partial save that hook is built to let through, and the rest of
   * the line survives untouched. Do not add fields here.
   *
   * The payload is written EXPLICITLY, null included: a tip that has aged out of
   * the backend's thirty-day window has to clear the stale receipt, and pinia-orm
   * merges an omitted key by keeping the old value.
   *
   * NEVER THROWS. The answer is a bonus on a screen that is already useful
   * without it, so a failure leaves the line exactly as it was.
   *
   * @param {string|number} itemId
   * @returns {Promise<object|null>} the suggestion payload, or null.
   */
  static async refreshSupplierSuggestion(itemId) {
    if (itemId == null) return null;

    try {
      const suggestion = await fetchSupplierSuggestionForItem(itemId);
      useRepo(OrderBoxItem).save({ id: String(itemId), supplierSuggestion: suggestion });
      return suggestion;
    } catch (err) {
      console.warn('refreshSupplierSuggestion: failed (non-fatal)', err);
      return null;
    }
  }

  /**
   * Update single item in order box
   * API: PATCH /order-boxes/{orderBoxId}/items/{itemId}
   * Expected request: { qty?, notes?, status? }
   * Expected response: { data: OrderBoxItem }
   */
  static async updateItem(orderBoxId, itemId, updates) {
    try {
      updates['id'] = itemId;
      const jsonData = dataFormatter.serialize(updates, 'order-box-items');
      await useAxiosRepo(OrderBoxItem).api().patch(
        `/order-box-items/${itemId}`,
        {
          data: jsonData.data,
          save: true,
        },
      );

      // Refresh box to get updated totals
      await this.fetchOne(orderBoxId);

      return useRepo(OrderBoxItem).find(itemId);
    } catch (error) {
      console.error('Failed to update item:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorUpdating');
    }
  }

  /**
   * Remove item from order box
   * API: DELETE /order-boxes/{orderBoxId}/items/{itemId}
   * Expected response: 204 No Content or { success: true }
   */
  static async removeItem(orderBoxId, itemId) {
    try {
      await useAxiosRepo(OrderBoxItem).api().delete(
        `/order-boxes/${orderBoxId}/items/${itemId}`,
      );

      // Manually remove from store
      useRepo(OrderBoxItem).destroy(itemId);

      // Refresh box to get updated totals
      return await this.fetchOne(orderBoxId);
    } catch (error) {
      console.error('Failed to remove item:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorUpdating');
    }
  }

  // =============================================================================
  // DELETE OPERATIONS
  // =============================================================================

  /**
   * Close/Cancel order box
   * API: POST /order-boxes/{id}/close
   * Expected response: { data: { id, status: 'Closed', ... } }
   */
  static async close(id) {
    try {
      await this.axiosRepo().api().post(`/order-boxes/${id}/close`, {
        save: true,
      });

      return this.repo().find(id);
    } catch (error) {
      console.error('Failed to close box:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorClosing');
    }
  }

  /**
   * Permanently delete order box (admin only)
   * API: DELETE /order-boxes/{id}
   * Expected response: 204 No Content
   */
  static async destroy(id) {
    try {
      await this.axiosRepo().api().delete(`/order-boxes/${id}`);

      // Manually remove from store
      this.repo().destroy(id);

      return true;
    } catch (error) {
      console.error('Failed to delete box:', error);
      throw errorFromJsonApi(error, 'box.feedback.errorDeleting');
    }
  }
}
